import { TEAM_PATHS } from '@/domains/admin';
import { LOGIN_ROUTE, REGISTRATION_ROUTE, addSurveyRoute, type Permission } from '@/domains/auth';
import { PORTAL_PATHS } from '@/domains/survey';
import { DEMO_PATHS } from './paths';

/**
 * The demo's walkthrough: twenty-three steps in five acts, from an advisor signing in to a ranking
 * published — the owner's brief for the two portals lists twenty-two, and the owner added the
 * updated-metrics collection (WF-10) after it, where the cycle has it: after the DD calls and the
 * firm exchange's contender reports, before the ranking is built (WF-07 steps 9–10). The guide
 * draws them in this order, each with its status and a jump to its screen.
 *
 * **`live` is a screen that reads survey-api; `mock` is one that reads the demo's fixtures and
 * says so on itself.** A step's status is the step's, not its zone's: act 1 is half live, and
 * act 5 is a live step beside a mock one.
 *
 * A `Record`, so a step without a status, a place or an act is a compile error — the onboarding
 * study's `SCREEN_MARKS` rule, for the same reason: a step that silently lost its status is the one
 * nobody notices is invented.
 */
export type DemoStep =
  | 'sign-in'
  | 'register-fa'
  | 'register-team'
  | 'survey-cycle'
  | 'documents'
  | 'account-and-help'
  | 'people-hub'
  | 'hub-rankings'
  | 'responses'
  | 'firms'
  | 'compliance-queues'
  | 'compliance-review'
  | 'my-tasks'
  | 'lists'
  | 'initial-review'
  | 'reinstatement'
  | 'dd-calls'
  | 'metrics-updates'
  | 'import'
  | 'rankings'
  | 'reports'
  | 'setup-live'
  | 'setup-mock';

export type DemoAct = 1 | 2 | 3 | 4 | 5;

export const DEMO_ACTS: Record<DemoAct, string> = {
  1: 'Advisor Portal',
  2: 'Team Portal: records',
  3: 'Team Portal: compliance',
  4: 'Team Portal: ranking operations',
  5: 'Team Portal: setup'
};

/** Who can open a step's screen — the session the jump needs, since the guards stay real. A
 *  Team Portal step also names the permission its screen's guard asks (`needs`). */
export type StepAudience = 'signed-out' | 'advisor' | 'team';

export const STEP_AUDIENCE_LABEL: Record<StepAudience, string> = {
  'signed-out': 'Signed out',
  advisor: 'As an advisor',
  team: 'As a Team Portal account'
};

/** The permission a Team Portal step's jump (`to`) is guarded by — its route's `AdminRoute`, read
 *  off the zone's routes and the demo's own — and, in words, what a role must include for it. */
export interface IStepNeed {
  permission: Permission;
  /** Completes "Your Team Portal roles do not include …". */
  what: string;
}

/** People, Responses, the queues and every mock work area: the participant surface's guard. */
const BROWSE: IStepNeed = {
  permission: 'responses.browse',
  what: 'browsing people and responses'
};

export interface IStepLink {
  label: string;
  to: string;
}

export interface IDemoStep {
  act: DemoAct;
  /** Its place in the walkthrough, 1–23. */
  number: number;
  status: 'live' | 'mock';
  title: string;
  /** What to do there, in a line. */
  what: string;
  to: string;
  /** The other screens the step covers, each a link of its own beside the jump. */
  also?: readonly IStepLink[];
  audience: StepAudience;
  /** For a Team Portal step, the permission its jump needs. */
  needs?: IStepNeed;
}

export const DEMO_STEPS: Record<DemoStep, IDemoStep> = {
  'sign-in': {
    act: 1,
    number: 1,
    status: 'live',
    title: 'Sign in, ask for an invitation',
    what: 'The front door: sign-in at Invent, and "No invitation yet? Ask for one".',
    to: LOGIN_ROUTE,
    audience: 'signed-out'
  },
  'register-fa': {
    act: 1,
    number: 2,
    status: 'live',
    title: 'Register by your numbers',
    what: 'Tick a CRD, an NPN or licence, both or none; a CRD is confirmed against BrokerCheck before an account exists.',
    to: REGISTRATION_ROUTE,
    audience: 'signed-out'
  },
  'register-team': {
    act: 1,
    number: 3,
    status: 'live',
    title: 'Register a team',
    what: 'Signed in, from “Add a survey”: the lead advisor’s CRD, then the gating questions: a wire firm goes to Wires, the rest to Independent, or to the RIA firm survey.',
    to: addSurveyRoute('TEAMS'),
    audience: 'advisor'
  },
  'survey-cycle': {
    act: 1,
    number: 4,
    status: 'live',
    title: 'Home → My surveys: the 2027 cycle',
    what: 'Start the survey, confirm last year’s answers, watch it save, submit, read the receipt.',
    to: PORTAL_PATHS.home,
    audience: 'advisor'
  },
  documents: {
    act: 1,
    number: 5,
    status: 'live',
    title: 'Documents',
    what: 'The PDF of each submitted survey, as the advisor saw it — nothing Veritant adds.',
    to: PORTAL_PATHS.documents,
    audience: 'advisor'
  },
  'account-and-help': {
    act: 1,
    number: 6,
    status: 'mock',
    title: 'Help: reach Veritant',
    what:
      'The contact block with the reference to quote. Account says our team changes a login ' +
      'address, and links here.',
    to: PORTAL_PATHS.help,
    also: [{ label: 'Account', to: PORTAL_PATHS.account }],
    audience: 'advisor'
  },
  'people-hub': {
    act: 2,
    number: 7,
    status: 'live',
    title: 'People → the person hub',
    what:
      'Open anyone from the list; a person’s record opens beside it — the record’s own tab and one ' +
      'tab per survey they hold, its Veritant research with Rankings and Asset & production — ' +
      'each section a fold in Salesforce’s order, one Actions menu, the dates, the photo and a ' +
      'copyable id in its header. A team or a firm opens on its own: its Advisors, or its SEC ' +
      'ADV entry and Principals.',
    to: TEAM_PATHS.people,
    audience: 'team',
    needs: BROWSE
  },
  'hub-rankings': {
    act: 2,
    number: 8,
    status: 'live',
    title: 'Rankings on the hub',
    what: 'Each survey’s placements by year inside its tab’s Veritant research — real data, edited in place, a reason and a history per cell.',
    to: TEAM_PATHS.people,
    audience: 'team',
    needs: BROWSE
  },
  responses: {
    act: 2,
    number: 9,
    status: 'live',
    title: 'Responses, with flags, notes and corrections',
    what:
      'A response opened as its questionnaire in the Team Portal; a submitted answer is corrected ' +
      'on its Answers tab, one at a time, each with a reason and a history.',
    to: TEAM_PATHS.responses,
    audience: 'team',
    needs: BROWSE
  },
  firms: {
    act: 2,
    number: 10,
    status: 'live',
    title: 'Firms',
    what:
      'The RIA firms and organisations we hold — their numbers, principals, SEC ADV match and ' +
      'latest response.',
    to: TEAM_PATHS.firms,
    audience: 'team',
    needs: BROWSE
  },
  'compliance-queues': {
    act: 3,
    number: 11,
    status: 'live',
    title: 'Compliance queues',
    what: 'The work items, a tab per queue: what raised each, who has it, when it is due, the state it stands in.',
    to: TEAM_PATHS.compliance,
    audience: 'team',
    needs: BROWSE
  },
  'compliance-review': {
    act: 3,
    number: 12,
    status: 'live',
    title: 'Reviewing one item',
    what: 'An item beside its queue: what raised it, its history; keep, remove, escalate, or wait for the firm.',
    to: `${TEAM_PATHS.compliance}?survey=FA`,
    audience: 'team',
    needs: BROWSE
  },
  'my-tasks': {
    act: 4,
    number: 13,
    status: 'live',
    title: 'My tasks',
    what: 'Everything assigned to you — compliance work items, DD calls, reinstatements and Lists rows — by type, each worked beside the list.',
    to: TEAM_PATHS.tasks,
    audience: 'team',
    needs: BROWSE
  },
  lists: {
    act: 4,
    number: 14,
    status: 'live',
    title: 'Lists',
    what: 'The Conga grid over the list engine: survey tabs, saved views, a funnel on every column, cells held until Save data and saved as one batch, a colleague’s save asked about cell by cell.',
    to: TEAM_PATHS.lists,
    audience: 'team',
    needs: BROWSE
  },
  'initial-review': {
    act: 4,
    number: 15,
    status: 'mock',
    title: 'Initial review, with "in consideration" thresholds',
    what: 'The survey owner’s AUM and production thresholds flag records while the survey is open.',
    to: DEMO_PATHS.initialReview,
    audience: 'team',
    needs: BROWSE
  },
  reinstatement: {
    act: 4,
    number: 16,
    status: 'live',
    title: 'Reinstatement',
    what: 'The advisors out for a reason that comes back each cycle, by reason, with the notes, dates and firms on the list; reinstate or keep out, in bulk where the reason allows it.',
    to: TEAM_PATHS.reinstatement,
    audience: 'team',
    needs: BROWSE
  },
  'dd-calls': {
    act: 4,
    number: 17,
    status: 'live',
    title: 'DD calls',
    what: 'The DD list — submitted surveys whose DD call box is not ticked — read through Lists; on the record page, the outreach, the interview against its template and the quality score in the same save.',
    to: TEAM_PATHS.ddCalls,
    also: [{ label: 'DD templates', to: TEAM_PATHS.ddTemplates }],
    audience: 'team',
    needs: { permission: 'internal_fields.read', what: 'reading staff fields' }
  },
  import: {
    act: 4,
    number: 18,
    status: 'live',
    title: 'Import, and the export layouts',
    what: 'A firm’s file back: checked against the records, its anomalies held, applied, reversed — the file it answers built by a list’s export in the firm’s layout.',
    to: TEAM_PATHS.imports,
    also: [{ label: 'Export layouts', to: TEAM_PATHS.exportLayouts }],
    audience: 'team',
    needs: { permission: 'firm_exchange.manage', what: 'exchanging files with firms' }
  },
  'metrics-updates': {
    act: 4,
    number: 19,
    status: 'live',
    title: 'Metrics updates',
    what: 'The round’s updated-metrics list and its exceptions, by an edition’s threshold; a request recorded, figures entered or confirmed unchanged; the firms that supply their advisors’ figures.',
    to: TEAM_PATHS.metricsUpdates,
    audience: 'team',
    needs: BROWSE
  },
  rankings: {
    act: 4,
    number: 20,
    status: 'mock',
    title: 'Rankings',
    what: 'Placements from the imported result; remove or insert renumbers everyone below; publish.',
    to: DEMO_PATHS.rankings,
    audience: 'team',
    needs: BROWSE
  },
  reports: {
    act: 4,
    number: 21,
    status: 'live',
    title: 'Reports',
    what: 'A report view’s definition, its preview and its kept runs; a list saved as a report, a report opened as a list.',
    to: TEAM_PATHS.reports,
    audience: 'team',
    needs: BROWSE
  },
  'setup-live': {
    act: 5,
    number: 22,
    status: 'live',
    title: 'Survey builder, Cycles, Users & roles, Concern criteria, BrokerCheck refresh, Email templates',
    what:
      'Setup: the authoring surface, the ranking cycles and their pins, the accounts and ' +
      'the Team Portal roles that bundle what each may do, what raises a compliance item, the ' +
      'scheduled BrokerCheck refresh with its runs, and the submission confirmation email.',
    to: TEAM_PATHS.builder,
    also: [
      { label: 'Team Portal roles', to: TEAM_PATHS.roles },
      { label: 'Concern criteria', to: TEAM_PATHS.criteria },
      { label: 'BrokerCheck refresh', to: TEAM_PATHS.refresh },
      { label: 'Email templates', to: TEAM_PATHS.mailTemplates }
    ],
    audience: 'team',
    needs: { permission: 'definitions.author', what: 'authoring surveys' }
  },
  'setup-mock': {
    act: 5,
    number: 23,
    status: 'mock',
    title: 'In-consideration thresholds',
    what:
      'The in-consideration thresholds, a mock panel inside the live Concern criteria. The ' +
      'outreach emails are live: Your templates in Email templates.',
    to: TEAM_PATHS.criteria,
    also: [{ label: 'Email templates', to: TEAM_PATHS.mailTemplates }],
    audience: 'team',
    needs: { permission: 'compliance.configure', what: 'configuring compliance' }
  }
};

/** The steps in the walkthrough's order. */
export const STEPS_IN_ORDER: readonly (IDemoStep & { key: DemoStep })[] = (
  Object.entries(DEMO_STEPS) as [DemoStep, IDemoStep][]
)
  .map(([key, step]) => ({ ...step, key }))
  .sort((a, b) => a.number - b.number);

/** What the walkthrough leaves out, and why — said once, in the guide. */
export const OUT_OF_THE_DEMO =
  'Out of the demo: events, Cvent, Lead Tracker, Granola and Qdeck — none has a contract yet.';
